import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';

const ReminderList = ({ reminders }) => {
  const navigate = useNavigate();

  // Format reminder messages based on type and data
  const formatReminderMessage = (item) => {
    const dueDate = new Date(
      item.due_date || item.period_to || item.emi_date || item.payment_due_date
    );
    const today = new Date();
    const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

    if (item.due_date) {
      return `${item.company || 'Utility Bill'} #${item.account_no || 'N/A'} due in ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    } else if (item.period_to) {
      return `${item.insurance_company || 'Insurance'} expiring in ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    } else if (item.emi_date) {
      return `${item.company_name || 'Loan'} #${item.loan_account_no || 'N/A'} EMI due in ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    } else if (item.payment_due_date) {
      return `${item.company || 'Rent'} #${item.id || 'N/A'} payment due in ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    }
    return `Reminder #${item.id || 'N/A'} due in ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
  };

  // Handle click on a reminder to navigate to the corresponding table with record ID and tab index
  const handleReminderClick = (item) => {
    let route;
    let tabIndex;

    if (item.due_date) {
      route = `/view-all/utilities/${item.id}`;
      tabIndex = 3; // Bills & Cards tab
    } else if (item.period_to) {
      route = `/view-all/insurance/${item.id}`;
      tabIndex = 0; // Insurance Policies tab
    } else if (item.emi_date) {
      route = `/view-all/loans/${item.id}`;
      tabIndex = 1; // Loan Payments tab
    } else if (item.payment_due_date) {
      route = `/view-all/rents/${item.id}`;
      tabIndex = 2; // Rent Payments tab
    }

    if (route) {
      navigate(route, { state: { tab: tabIndex } });
    }
  };

  return (
    <Paper
  elevation={0}
  sx={{
    p: 3,
    borderRadius: '3rem',
    height: 396,
    width: '200%', // Changed from '200%'
    maxWidth: '2000px', // Adjusted to fit within md={6}
    overflowY: 'hidden',
    overflowX: 'hidden',
    position: 'relative',
    background: 'linear-gradient(to right bottom, rgba(7, 7, 7, 0.9), rgba(50, 50, 50, 0.5))',
    fontFamily: '"Rockwell", "Courier New", Courier, Georgia, Times, "Times New Roman", serif',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '-10rem',
      left: '-10rem',
      width: '40rem',
      height: '40rem',
      borderRadius: '50%',
      background: 'rgba(255, 255, 255, 0.1)',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: '0rem',
      right: '-10rem',
      width: '40rem',
      height: '40rem',
      marginBottom: '-25rem',
      borderRadius: '50%',
      background: 'rgba(31, 30, 30, 0.6)',
    },
    '&::-webkit-scrollbar': {
      width: 0,
    },
  }}
>
      <Box
        display="flex"
  alignItems="center"
  gap={1}
  mb={2}
  sx={{
    position: 'sticky',
    top: 0,
    padding: '2rem 0',
    paddingTop: '1rem',
    textAlign: 'center',
    color: '#fff',
    marginBottom: '1rem',
        }}
      >
        <NotificationsActiveIcon color="error" />
        <Typography variant="h6" fontWeight={600}>
          Reminders
        </Typography>
      </Box>
      <List
        disablePadding
        sx={{
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          alignItems: 'center',
          listStyle: 'none',
          counterReset: 'my-counter',
        }}
      >
        {reminders.length > 0 ? (
          reminders.map((item, index) => (
            <ListItem
  key={item.id || index}
  sx={{
   
    textAlign: 'center',
    color: '#fff',
    borderRadius: '3rem',
    background: 'rgba(10, 10, 10, 0.7)',
    padding: '1.1rem 1.5rem',
    counterIncrement: 'my-counter',
    zIndex: 10,
    position: 'relative',
    overflow: 'hidden',
    transition: 'color 0.3s ease-in-out',
    '&::before': {
      content: `"${index + 1}. "`,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      width: 0,
      height: '100%',
      background: 'linear-gradient(to left, #e0b256 20%, transparent 80%)',
      transition: 'width 0.4s ease',
      zIndex: -1,
    },
    '&:hover::after': {
      width: '%', // Only cover half
    },
    '&:hover': {
      cursor: 'pointer',
       // Optional text color change
    },
  }}

              onClick={() => handleReminderClick(item)}
            >
              <ListItemText
                primary={
                  <Typography variant="body1" fontWeight={500}>
                    {formatReminderMessage(item)}
                  </Typography>
                }
              />
            </ListItem>
          ))
        ) : (
          <ListItem
            sx={{
              width: '40rem',
              textAlign: 'center',
              color: '#fff',
              borderRadius: '3rem',
              background: 'rgba(12, 12, 12, 0.7)',
              padding: '1.1rem 1.5rem',
            }}
          >
            <ListItemText
              primary={
                <Typography variant="body1" color="#fff">
                  No upcoming reminders
                </Typography>
              }
            />
          </ListItem>
        )}
      </List>
    </Paper>
  );
};

export default ReminderList;